import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { lockTerminal, switchUserTerminal, unlockTerminal } from '../../store/slices/uiSlice';
import {
  User,
  LogOut,
  ChevronDown,
  Lock,
  Users,
  CalendarRange,
  KeyRound,
} from 'lucide-react';
import CommandChangePasswordModal from './CommandChangePasswordModal';

export default function UserDropdown() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fullName = user ? `${user.firstName} ${user.lastName}` : 'User';
  const returnPath = `${location.pathname}${location.search}`;

  const closeMenu = () => setIsOpen(false);

  const handleLockTerminal = () => {
    if (!user) return;
    dispatch(lockTerminal({ returnPath, lockedUserId: user.id }));
    closeMenu();
  };

  const handleSwitchUser = () => {
    dispatch(switchUserTerminal({ returnPath }));
    closeMenu();
  };

  const handleChangeEvent = () => {
    sessionStorage.removeItem('c2_active_event_id');
    closeMenu();
    navigate('/');
  };

  const handleChangePassword = () => {
    closeMenu();
    setShowChangePassword(true);
  };

  const handleLogout = () => {
    closeMenu();
    dispatch(unlockTerminal());
    dispatch(logout());
    navigate('/login');
  };

  const menuButtonClass =
    'w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-left text-sm font-medium text-foreground';

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-accent transition-colors"
        >
          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="w-3 h-3 text-primary" />
          </div>
          <span className="text-xs font-medium text-foreground hidden xl:inline">
            {fullName}
          </span>
          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-[9999]">
            <div className="p-3 border-b border-border">
              <p className="text-sm font-medium text-foreground">{fullName}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground mt-1 capitalize">{user?.role.replace(/_/g, ' ')}</p>
            </div>
            <div className="p-1">
              <button type="button" onClick={handleLockTerminal} className={menuButtonClass}>
                <Lock className="w-4 h-4 text-muted-foreground" />
                Lock terminal
              </button>
              <button type="button" onClick={handleSwitchUser} className={menuButtonClass}>
                <Users className="w-4 h-4 text-muted-foreground" />
                Switch user
              </button>

              <div className="my-1 border-t border-border" />

              <button type="button" onClick={handleChangeEvent} className={menuButtonClass}>
                <CalendarRange className="w-4 h-4 text-muted-foreground" />
                Change event
              </button>
              <button type="button" onClick={handleChangePassword} className={menuButtonClass}>
                <KeyRound className="w-4 h-4 text-muted-foreground" />
                Change password
              </button>

              <div className="my-1 border-t border-border" />

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors text-left"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Log out</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <CommandChangePasswordModal
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </>
  );
}
